import React from 'react';
import {
  ButtonIcon,
  IconChevronDown,
  IconChevronUp,
  IconX,
} from '@spotify-internal/encore-web';
import { UserState } from './types';
import { Link } from '../../ui';
import { RequireFeatureFlag } from '../../Auth/RequireFeatureFlag';
import {
  CloseSideMenuButton,
  StyledSideMenu,
  SideMenuAction,
  SidemenuSeparator,
  SideMenuLink,
  SideMenuCategory,
  SideMenuCategoryTitle,
  SideMenuCategoryItem,
} from './NavSideMenu.styles';
import { USE_SEARCH } from './constants';
import { isHelpLive } from '../../Help/util';
import { useT } from '@spotify-internal/i18n-core';
import { useRtl } from '../../../../features/src/i18n';
import { getAccountsDomain } from '../../../../features/src/auth';
import { SideMenuLanguageSection } from './Language';
import { NavArrowIcon } from './utils';

const TRACK_CAT = 'Side Nav';

type NavSideMenuProps = {
  userState: UserState;
  onClose(event: any): void;
};

type MenuState = {
  features?: boolean;
  resources?: boolean;
  help?: boolean;
};

export const NavSideMenu: React.FC<NavSideMenuProps> = ({
  userState,
  onClose,
}) => {
  const t = useT();
  const rtl = useRtl();
  const showLogin = userState === 'loggedOut';
  const showLogout = userState === 'claimed' || userState === 'unclaimed';
  const showDashboard = userState === 'claimed';
  const showClaim = userState === 'unclaimed' || userState === 'loggedOut';
  const [menuState, setMenuState] = React.useState<MenuState>({});

  const toggleMenu = (key: keyof MenuState) => {
    setMenuState(cur => ({ ...cur, [key]: !cur[key] }));
  };

  return (
    <StyledSideMenu
      dir={rtl ? 'rtl' : 'ltr'}
      id="nav-sidemenu"
      tabIndex={0}
      role="dialog"
      aria-label={t(
        'FRODOR_NAV_RESPONSIVE_LABEL',
        'Navigation dialog',
        'Label for responsive navigation dialog',
      )}
    >
      <CloseSideMenuButton
        tabIndex={0}
        id="close-sidemenu"
        onClick={onClose}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key === ' ' || e.key === 'Enter') {
            e.preventDefault();
            onClose(e);
          }
        }}
        aria-label={t(
          'FRODOR_NAV_RESPONSIVE_CLOSE',
          'Close navigation menu',
          'close navigation menu in responsive display',
        )}
        iconOnly={IconX}
        condensedAll
      />

      <ul>
        {showClaim && (
          <li>
            <SideMenuAction>
              <Link
                data-testid="navigation-sidemenu-claim"
                track={[TRACK_CAT, `Get Access (${userState})`]}
                href="/claim"
                prefetch={false}
              >
                {t('FRODOR_NAVBAR_SIDE_GET_ACCESS', 'Get access', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showLogout && (
          <li>
            <SideMenuAction>
              <Link
                data-testid="navigation-sidemenu-logout"
                track={[TRACK_CAT, 'Log Out']}
                href={`${getAccountsDomain()}/logout?continue=${encodeURIComponent(
                  window.location.href,
                )}`}
                prefetch={false}
              >
                {t('FRODOR_NAVBAR_SIDE_LOGOUT', 'Log out', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showLogin && (
          <li>
            <SideMenuAction>
              <Link
                data-testid="navigation-sidemenu-login"
                track={[TRACK_CAT, 'Log In']}
                href={`${getAccountsDomain()}/login?continue=${encodeURIComponent(
                  window.location.href,
                )}`}
                prefetch={false}
              >
                {t('FRODOR_NAVBAR_SIDE_LOGIN', 'Log in', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}
        {showDashboard && (
          <li>
            <SideMenuAction>
              <Link
                track={[TRACK_CAT, 'Dashboard']}
                href="/c"
                prefetch={false}
                data-testid="navigation-sidemenu-dashboard"
              >
                {t('FRODOR_NAVBAR_SIDE_DASHBOARD', 'Dashboard', '')}
              </Link>
            </SideMenuAction>
          </li>
        )}

        <li>
          <SidemenuSeparator />
        </li>

        <li>
          <SideMenuLink>
            <Link
              track={[TRACK_CAT, 'GetStarted']}
              href="/get-started"
              data-testid="navigation-sidemenu-get-started"
              prefetch={false}
            >
              {t('FRODOR_NAVBAR_GET_STARTED', 'Get started', '')}
            </Link>
          </SideMenuLink>
        </li>

        {/* Features menu */}
        <li>
          <SideMenuCategory>
            <ButtonIcon onClick={() => toggleMenu('features')}>
              <SideMenuCategoryTitle down={!!menuState.features}>
                {t('FRODOR_NAVBAR_SIDE_FEATURES', 'Features', '')}
                {menuState.features ? (
                  <IconChevronUp
                    iconSize={24}
                    aria-label={t(
                      'FRODOR_NAVBAR_SIDE_FEATURES_CLOSE',
                      'Close features menu',
                      '',
                    )}
                  />
                ) : (
                  <IconChevronDown
                    iconSize={24}
                    aria-label={t(
                      'FRODOR_NAVBAR_SIDE_FEATURES_OPEN',
                      'Open features menu',
                      '',
                    )}
                  />
                )}
              </SideMenuCategoryTitle>
            </ButtonIcon>
            {menuState.features && (
              <ul>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'features-overview']}
                      href="/features"
                      data-testid="navigation-sidemenu-overview"
                    >
                      {t('FRODOR_NAVBAR_FEATURES_OVERVIEW', 'Overview', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      track={[TRACK_CAT, 'canvas']}
                      href="/canvas"
                      data-testid="navigation-sidemenu-canvas"
                    >
                      Canvas <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      track={[TRACK_CAT, 'clips']}
                      href="/clips"
                      data-testid="navigation-sidemenu-clips"
                    >
                      Clips <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'discovery-mode']}
                      href="/discovery-mode"
                      data-testid="navigation-sidemenu-discovery-mode"
                    >
                      Discovery Mode
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      data-testid="navigation-sidemenu-marquee"
                      track={[TRACK_CAT, 'Marquee']}
                      href="/marquee"
                    >
                      Marquee
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      data-testid="navigation-sidemenu-merch"
                      track={[TRACK_CAT, 'Merch']}
                      href="/merch"
                    >
                      {t('FRODOR_NAVBAR_MERCH_LINK', 'Merch', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      data-testid="navigation-sidemenu-playlisting"
                      track={[TRACK_CAT, 'Playlisting']}
                      href="/playlisting"
                    >
                      {t('FRODOR_NAVBAR_PLAYLISTING_LINK', 'Playlisting', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-promocards"
                      track={[TRACK_CAT, 'Promo Cards']}
                      href="/promocards"
                    >
                      {t(
                        'FRODOR_NAVBAR_SIDE_PROMO_CARDS',
                        'Promo Cards',
                        'Promo is short for promotional',
                      )}{' '}
                      <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>
              </ul>
            )}
          </SideMenuCategory>
        </li>

        {/* Resources menu */}
        <li>
          <SideMenuCategory>
            <ButtonIcon onClick={() => toggleMenu('resources')}>
              <SideMenuCategoryTitle down={!!menuState.resources}>
                {t('FRODOR_NAVBAR_SIDE_RESOURCES_MENU', 'Resources', '')}
                {menuState.resources ? (
                  <IconChevronUp
                    iconSize={24}
                    aria-label={t(
                      'FRODOR_NAVBAR_SIDE_RESOURCES_MENU_CLOSE',
                      'Close resources menu',
                      '',
                    )}
                  />
                ) : (
                  <IconChevronDown
                    iconSize={24}
                    aria-label={t(
                      'FRODOR_NAVBAR_SIDE_RESOURCES_MENU_OPEN',
                      'Open resources menu',
                      '',
                    )}
                  />
                )}
              </SideMenuCategoryTitle>
            </ButtonIcon>
            {menuState.resources && (
              <ul>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Videos']}
                      href="/videos"
                      data-testid="navigation-sidemenu-videos"
                    >
                      {t('FRODOR_NAVBAR_SIDE_VIDEOS', 'Videos', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>
                <li>
                  <SideMenuCategoryItem>
                    <Link
                      track={[TRACK_CAT, 'Blog']}
                      href="/blog"
                      data-testid="navigation-sidemenu-blog"
                    >
                      {t('FRODOR_NAVBAR_SIDE_NEWS', 'News', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      data-testid="navigation-sidemenu-events"
                      track={[TRACK_CAT, 'Events']}
                      href="/events"
                    >
                      {t('FRODOR_NAVBAR_SIDE_EVENTS', 'Events', '')}
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-infocus"
                      track={[TRACK_CAT, 'In Focus']}
                      href="/infocus?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      In Focus <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-fanstudy"
                      track={[TRACK_CAT, 'Fan Study']}
                      href="/fanstudy?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Fan Study <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-madetobefound"
                      track={[TRACK_CAT, 'Made to Be Found']}
                      href="/found?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Made to Be Found <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-loudclear"
                      track={[TRACK_CAT, 'Loud & Clear']}
                      href="/loudandclear?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Loud & Clear <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>

                <li>
                  <SideMenuCategoryItem>
                    <Link
                      external
                      data-testid="navigation-noteable"
                      track={[TRACK_CAT, 'Noteable']}
                      href="/noteable?utm_campaign=side-nav&utm_medium=owned-product&utm_source=s4a"
                    >
                      Noteable <NavArrowIcon />
                    </Link>
                  </SideMenuCategoryItem>
                </li>
              </ul>
            )}
          </SideMenuCategory>
        </li>

        {/* Help link */}

        {isHelpLive && (
          <li>
            <SideMenuLink>
              <Link
                track={[TRACK_CAT, 'Help']}
                href="/help"
                data-testid="navigation-sidemenu-help"
              >
                {t('FRODOR_NAVBAR_SIDE_HELP_LIVE', 'Help', '')}
              </Link>
            </SideMenuLink>
          </li>
        )}

        {!isHelpLive && (
          <li>
            <SideMenuCategory>
              <ButtonIcon onClick={() => toggleMenu('help')}>
                <SideMenuCategoryTitle down={!!menuState.help}>
                  {t('FRODOR_NAVBAR_SIDE_HELP_NOT_LIVE', 'Help', '')}
                  {menuState.help ? (
                    <IconChevronUp
                      iconSize={24}
                      aria-label={t(
                        'FRODOR_NAVBAR_SIDE_CLOSE_HELP',
                        'Close help menu',
                        '',
                      )}
                    />
                  ) : (
                    <IconChevronDown
                      iconSize={24}
                      aria-label={t(
                        'FRODOR_NAVBAR_SIDE_OPEN_HELP',
                        'Open help menu',
                        '',
                      )}
                    />
                  )}
                </SideMenuCategoryTitle>
              </ButtonIcon>
              {menuState.help && (
                <ul>
                  <li>
                    <SideMenuCategoryItem>
                      <Link
                        track={[TRACK_CAT, 'Guide']}
                        href="/guide"
                        data-testid="navigation-sidemenu-guide"
                      >
                        {t('FRODOR_NAVBAR_SIDE_GUIDE', 'Guide', '')}
                      </Link>
                    </SideMenuCategoryItem>
                  </li>
                  <li>
                    <SideMenuCategoryItem>
                      <Link
                        track={[TRACK_CAT, 'FAQs']}
                        href="/faq"
                        data-testid="navigation-sidemenu-faq"
                      >
                        FAQ
                      </Link>
                    </SideMenuCategoryItem>
                  </li>
                </ul>
              )}
            </SideMenuCategory>
          </li>
        )}

        {USE_SEARCH && (
          <RequireFeatureFlag
            flag="s4a-frodor-test"
            fallback={<></>}
            waiting={<></>}
          >
            <li>
              <SideMenuLink>
                <Link
                  track={[TRACK_CAT, 'Search']}
                  href="/search"
                  data-testid="navigation-sidemenu-search"
                  prefetch={false}
                >
                  {t('FRODOR_NAVBAR_SIDE_SEARCH', 'Search', '')}
                </Link>
              </SideMenuLink>
            </li>
          </RequireFeatureFlag>
        )}
      </ul>
      <SideMenuLanguageSection onButtonClick={onClose} />
    </StyledSideMenu>
  );
};
